import { Modal, Button, Form } from "react-bootstrap";
import { useState, useEffect } from "react";
import { fetchWithAuth } from "../api/fetchWithAuth";

const typeOptions = [
  { value: "", label: "-" },
  { value: "Functional", label: "Functional" },
  { value: "Non Functional", label: "Non Functional" },
  { value: "Info", label: "Info" },
  { value: "Performance", label: "Performance" }
];

const silOptions = [
  { value: "", label: "-" },
  { value: "0", label: "0" },
  { value: "1", label: "1" },
  { value: "2", label: "2" }
];

const classOptions = [
  { value: "", label: "-" },
  { value: "sys", label: "System" },
  { value: "sw", label: "Software" }
];

function normalizeCoverageIds(value) {
  if (!Array.isArray(value)) return [];

  return value
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id > 0);
}

function RequirementFormModal({ show, onClose, onSaved, requirement }) {

  const isEdit = !!requirement;

  const [form, setForm] = useState({
    number: "",
    name: "",
    type: "",
    sil: "",
    class: "",
    coverage: [],
    note: "",
    quote: "",
    status: "clarified",
    targetVersionId: ""
  });
  const [versions, setVersions] = useState([]);
  const [testCases, setTestCases] = useState([]);
  const [testCaseSearch, setTestCaseSearch] = useState("");

  useEffect(() => {
    if (requirement) {
      setForm({
        number: requirement.number ?? "",
        name: requirement.name ?? "",
        type: requirement.type ?? "",
        sil: requirement.sil ?? requirement.SIL ?? "",
        class: requirement.class ?? "",
        coverage: normalizeCoverageIds(requirement.coverage),
        note: requirement.note ?? "",
        quote: requirement.quote ?? "",
        status: requirement.status ?? "clarified",
        targetVersionId: requirement.targetVersionId ?? ""
      });
    } else {
      setForm({
        number: "",
        name: "",
        type: "",
        sil: "",
        class: "",
        coverage: [],
        note: "",
        quote: "",
        status: "clarified",
        targetVersionId: ""
      });
    }
  }, [requirement, show]);

  useEffect(() => {
    if (!show) return;

    const loadVersions = async () => {
      try {
        const res = await fetchWithAuth("/api/versions");

        if (!res.ok) {
          return;
        }

        const data = await res.json();
        setVersions(
          data
            .filter((version) => !version.closed)
            .sort((leftVersion, rightVersion) => {
              const activeCompare = Number(Boolean(rightVersion.isActive)) - Number(Boolean(leftVersion.isActive));
              if (activeCompare !== 0) return activeCompare;

              return String(leftVersion.name ?? "").localeCompare(
                String(rightVersion.name ?? ""),
                undefined,
                { sensitivity: "base", numeric: true }
              );
            })
        );
      } catch (err) {
        console.warn("loadVersions aborted:", err.message);
      }
    };

    loadVersions();
    setTestCaseSearch("");
  }, [show]);

  useEffect(() => {
    if (!show) return;

    const loadTestCases = async () => {
      try {
        const res = await fetchWithAuth("/api/test-cases?compact=1");

        if (!res.ok) {
          return;
        }

        const data = await res.json();
        setTestCases(data);
      } catch (err) {
        console.warn("loadTestCases aborted:", err.message);
      }
    };

    loadTestCases();
  }, [show]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((current) => {
      const nextForm = {
        ...current,
        [name]: value
      };

      if (name === "status" && (value === "clarified" || value === "needs_clarification" || value === "review")) {
        nextForm.targetVersionId = "";
      }

      return nextForm;
    });
  };

  const toggleCoverage = (testCaseId) => {
    setForm((current) => {
      const coverage = current.coverage.includes(testCaseId)
        ? current.coverage.filter((id) => id !== testCaseId)
        : [...current.coverage, testCaseId];

      return {
        ...current,
        coverage
      };
    });
  };

  const filteredTestCases = testCases.filter((testCase) => {
    const query = testCaseSearch.trim().toLowerCase();
    if (!query) return true;

    return (
      String(testCase.number ?? "").toLowerCase().includes(query) ||
      String(testCase.name ?? "").toLowerCase().includes(query)
    );
  });

  const handleSubmit = async () => {
    const url = isEdit
      ? `/api/requirements/${requirement.id}`
      : "/api/requirements";

    const method = isEdit ? "PUT" : "POST";

    const res = await fetchWithAuth(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...form,
        coverage: form.coverage.length ? form.coverage : null,
        targetVersionId: form.status === "targeted" && form.targetVersionId
          ? Number(form.targetVersionId)
          : null
      })
    });

    if (!res.ok) {
      const error = await res.json().catch(() => null);
      alert(error?.error || "Save failed");
      return;
    }

    onSaved();
    onClose();
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">

      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit ? "Edit Requirement" : "Add Requirement"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>

        <Form>

          <Form.Group className="mb-3">
            <Form.Label>Number</Form.Label>
            <Form.Control
              name="number"
              value={form.number}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              name="name"
              value={form.name}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Type</Form.Label>
            <Form.Select
              name="type"
              value={form.type}
              onChange={handleChange}
            >
              {typeOptions.map((option) => (
                <option key={option.value || "empty"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>SIL</Form.Label>
            <Form.Select
              name="sil"
              value={form.sil}
              onChange={handleChange}
            >
              {silOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Class</Form.Label>
            <Form.Select
              name="class"
              value={form.class}
              onChange={handleChange}
            >
              {classOptions.map((option) => (
                <option key={option.value || "empty"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Label>Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="note"
              value={form.note}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Quote</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              name="quote"
              value={form.quote}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Coverage</Form.Label>
            <Form.Control
              className="mb-2"
              value={testCaseSearch}
              onChange={(event) => setTestCaseSearch(event.target.value)}
              placeholder="Search test cases..."
            />
            <div className="small text-muted mb-2">
              {form.coverage.length} selected
            </div>
            <div
              className="border rounded p-2"
              style={{ maxHeight: "220px", overflowY: "auto" }}
            >
              {filteredTestCases.length === 0 ? (
                <div className="text-muted small">No test cases found.</div>
              ) : filteredTestCases.map((testCase) => (
                <Form.Check
                  key={testCase.id}
                  className="mb-2"
                  type="checkbox"
                  id={`coverage-test-case-${testCase.id}`}
                  checked={form.coverage.includes(Number(testCase.id))}
                  onChange={() => toggleCoverage(Number(testCase.id))}
                  label={(
                    <span>
                      <span className="fw-semibold">{testCase.number}</span>
                      <span className="text-muted"> - {testCase.name}</span>
                    </span>
                  )}
                />
              ))}
            </div>
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              name="status"
              value={form.status}
              onChange={handleChange}
            >
              <option value="raw">Raw</option>
              <option value="review">Review</option>
              <option value="clarified">Clarified</option>
              <option value="needs_clarification">Needs Clarification</option>
              <option value="targeted">Targeted</option>
              <option value="implemented">Implemented</option>
            </Form.Select>
          </Form.Group>

          {form.status === "targeted" && (
            <Form.Group className="mt-3">
              <Form.Label>Target</Form.Label>
              <Form.Select
                name="targetVersionId"
                value={form.targetVersionId}
                onChange={handleChange}
              >
                <option value="">Select target version...</option>
                {versions.map((version) => (
                  <option key={version.id} value={version.id}>
                    {version.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

        </Form>

      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>

        <Button variant="success" onClick={handleSubmit}>
          Save
        </Button>
      </Modal.Footer>

    </Modal>
  );
}

export default RequirementFormModal;
